import { useState } from "react";

import { AlertTriangle, ExternalLink, Loader2 } from "lucide-react";

import { RemoteSelect } from "@/components/fields/remote-select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ImageZoom } from "@/components/ui/shadcn-io/image-zoom";
import { useArtistRepository } from "@/pages/artists/hooks/use-artist-repository";
import type { ResolutionComponentProps } from "@/pages/tina/items/components/panels/hitl-panel/resolution-components";
import { useItemsRepository } from "@/pages/tina/items/hooks/use-items-repository";

import {
  type ArtistOption,
  type MemberAction,
  type MemberDecision,
  type SpotifyCandidate,
  allFlaggedResolved,
  buildArtistResolutionPayload,
  parseSpotifyArtistId,
  readFlaggedMembers,
  readLinkedMembers,
} from "./artist-resolution.helpers";

const ACTIONS: ReadonlyArray<{ action: MemberAction; label: string }> = [
  { action: "pick", label: "Pick existing" },
  { action: "spotify", label: "Spotify link" },
  { action: "unmatched", label: "Keep unmatched" },
  { action: "drop", label: "Drop" },
];

const FLAG_LABELS: Record<string, string> = {
  spotify_miss: "No Spotify match",
  spotify_no_image: "Spotify candidate has no image",
  ambiguous_unresolved: "Ambiguous match",
  unsafe_artist_match: "Ambiguous match",
  low_confidence: "Low confidence",
};

function flagLabel(flag?: string): string {
  return (flag && FLAG_LABELS[flag]) || "Needs review";
}

function SpotifyCandidateList({
  candidates,
  onPickExisting,
  onUseSpotify,
}: {
  candidates: SpotifyCandidate[];
  onPickExisting: (c: SpotifyCandidate) => void;
  onUseSpotify: (c: SpotifyCandidate) => void;
}) {
  if (candidates.length === 0) return null;
  return (
    <ul className="flex flex-col gap-2">
      {candidates.map((c) => (
        <li
          key={c.spotifyId}
          className="flex items-center gap-3 rounded-md border bg-muted/20 p-2"
        >
          {c.imageUrl ? (
            <ImageZoom className="shrink-0">
              <img
                src={c.imageUrl}
                alt={c.name}
                className="h-10 w-10 rounded-md border object-cover"
              />
            </ImageZoom>
          ) : (
            <span className="h-10 w-10 shrink-0 rounded-md border bg-muted" />
          )}
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex items-center gap-2">
              <span className="truncate font-medium text-foreground">
                {c.name}
              </span>
              {c.dbExists && (
                <span className="rounded-full border border-emerald-500/40 bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                  In our DB
                </span>
              )}
              {/* One-click open in a new tab. */}
              <a
                href={`https://open.spotify.com/artist/${c.spotifyId}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Open in Spotify (new tab)"
                className="inline-flex items-center gap-0.5 text-xs text-blue-500 hover:underline"
              >
                Spotify
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
            {c.genres.length > 0 && (
              <span className="truncate text-xs text-muted-foreground">
                {c.genres.join(", ")}
              </span>
            )}
          </div>
          <div className="flex shrink-0 gap-1">
            {c.dbExists && c.dbArtistId && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onPickExisting(c)}
              >
                Use this (in DB)
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => onUseSpotify(c)}
            >
              Create from Spotify
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
}

export function ArtistResolution({
  item,
  hitlContext,
  onResolved,
}: ResolutionComponentProps) {
  const { resolveHitl } = useItemsRepository();
  const { search } = useArtistRepository();
  const [decisions, setDecisions] = useState<Record<string, MemberDecision>>(
    {},
  );
  const [submitting, setSubmitting] = useState(false);

  const linked = readLinkedMembers(hitlContext);
  const flagged = readFlaggedMembers(hitlContext);
  const canSubmit =
    flagged.length > 0 && allFlaggedResolved(flagged, decisions);

  function setAction(name: string, action: MemberAction) {
    setDecisions((prev) => ({ ...prev, [name]: { ...prev[name], action } }));
  }

  function patch(name: string, partial: Partial<MemberDecision>) {
    setDecisions((prev) => ({
      ...prev,
      [name]: { ...prev[name], ...partial },
    }));
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      await resolveHitl(item.id, {
        resolution: buildArtistResolutionPayload(decisions),
      });
      onResolved();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Lineup · {linked.length + flagged.length} members · {linked.length}{" "}
        linked · {flagged.length} need review
      </p>

      {linked.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {linked.map((member) => (
            <span
              key={member.name}
              className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
            >
              {member.name}
            </span>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-4">
        {flagged.map((member) => {
          const decision = decisions[member.name];
          return (
            <div
              key={member.name}
              className="flex flex-col gap-2 rounded-md border p-3"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-foreground">
                  {member.name}
                </span>
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="h-3 w-3" />
                  {flagLabel(member.flag)}
                </span>
              </div>
              <div className="flex flex-wrap gap-1">
                {ACTIONS.map(({ action, label }) => (
                  <Button
                    key={action}
                    type="button"
                    size="sm"
                    variant={
                      decision?.action === action ? "default" : "outline"
                    }
                    onClick={() => setAction(member.name, action)}
                  >
                    {label}
                  </Button>
                ))}
              </div>
              <SpotifyCandidateList
                candidates={member.candidates}
                onPickExisting={(c) => {
                  setAction(member.name, "pick");
                  patch(member.name, {
                    artistId: c.dbArtistId,
                    pickedArtist: {
                      id: c.dbArtistId,
                      name: c.name,
                      media: c.imageUrl
                        ? { photos: [{ url: c.imageUrl }] }
                        : undefined,
                    },
                  });
                }}
                onUseSpotify={(c) => {
                  setAction(member.name, "spotify");
                  patch(member.name, {
                    spotifyRef: `https://open.spotify.com/artist/${c.spotifyId}`,
                  });
                }}
              />
              {decision?.action === "pick" && (
                <RemoteSelect<ArtistOption>
                  triggerType="change"
                  showSelected
                  value={decision.pickedArtist}
                  onValueChanged={(selected) =>
                    patch(member.name, {
                      artistId: selected?.id,
                      pickedArtist: selected,
                    })
                  }
                  accessorFn={(option) => ({
                    id: option?.id || option?.name || "",
                    name: option?.name || "",
                    iconUrl: option?.media?.photos?.[0]?.url,
                    number:
                      option?.stats?.spotify?.monthlyListeners?.toString(),
                  })}
                  onSearch={async (query, onStatusChanged) => {
                    onStatusChanged("loading");
                    const results = (await search(query)) as ArtistOption[];
                    onStatusChanged("success");
                    return results;
                  }}
                />
              )}
              {decision?.action === "spotify" && (
                <div className="flex flex-col gap-1">
                  <Input
                    value={decision.spotifyRef ?? ""}
                    onChange={(event) =>
                      patch(member.name, { spotifyRef: event.target.value })
                    }
                    placeholder="https://open.spotify.com/artist/… or spotify:artist:…"
                  />
                  {(decision.spotifyRef?.trim().length ?? 0) > 0 &&
                    parseSpotifyArtistId(decision.spotifyRef) === null && (
                      <span className="text-xs font-medium text-destructive">
                        That&apos;s not a Spotify artist link. Paste an
                        open.spotify.com/artist/… URL, a spotify:artist:… URI,
                        or the bare artist id.
                      </span>
                    )}
                </div>
              )}
              {decision?.action === "unmatched" && (
                <span className="text-xs text-muted-foreground">
                  Stays in the lineup as a plain text name, not linked.
                </span>
              )}
              {decision?.action === "drop" && (
                <span className="text-xs text-destructive">
                  Removed from the lineup entirely.
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-col gap-1">
        <Button
          type="button"
          disabled={!canSubmit || submitting}
          onClick={handleSubmit}
        >
          {submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
          Resolve &amp; resume
        </Button>
        <span className="text-xs text-muted-foreground">
          Re-runs artist_resolution with your per-member choices and continues
          to create.
        </span>
      </div>
    </div>
  );
}
